import React, { useState, useMemo, useEffect } from 'react';
import { View, TouchableOpacity, TextInput, ActivityIndicator, ScrollView } from 'react-native';
import { useQuery } from '@tanstack/react-query';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Modal from '../../../../components/Modal';
import IconButton from '../../../../components/IconButton';
import api from '../../../../utils/api';

const AREA_COLORS = ['#7C3AED', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#3B82F6', '#84CC16'];


export default function AreaEditModal({ communityId, area, onRequestClose, onSaved }: any) {
  const isEditing = !!area;

  const [name, setName] = useState(area?.name ?? '');
  const [color, setColor] = useState(area?.color ?? AREA_COLORS[0]);
  const [responsibleUserUuid, setResponsibleUserUuid] = useState<string | null>(
    area?.responsibleUser?.uuid ?? area?.responsible_user?.uuid ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const usersQuery = useQuery<any>({
    queryKey: ['admin-users', communityId],
    queryFn: () => api.get(`/communities/${communityId}/staff`).then(r => r.data),
    enabled: !!communityId,
  });

  const users: any[] = usersQuery.data ?? [];

  const onSave = async () => {
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      const responsible = users.find((u: any) => u.uuid === responsibleUserUuid);
      const payload = {
        name: name.trim(),
        color,
        responsible_user_id: responsible?.id ?? null,
      };
      if (isEditing) {
        await api.put(`/communities/${communityId}/ticket-areas/${area.uuid}`, payload);
      } else {
        await api.post(`/communities/${communityId}/ticket-areas`, payload);
      }
      onSaved();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al guardar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!confirm(`¿Eliminar el área "${area.name}"?`)) return;
    setIsSubmitting(true);
    try {
      await api.delete(`/communities/${communityId}/ticket-areas/${area.uuid}`);
      onSaved();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Error al eliminar');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal onRequestClose={onRequestClose} style={{ modal: tw`w-[480px] max-w-[90%]` }}>
      <View>
        <Text style={tw`text-lg font-600 text-secondary mb-4`}>{isEditing ? 'Editar área' : 'Nueva área'}</Text>

        <Text style={tw`text-gray-500 mb-2`}>Nombre</Text>
        <TextInput
          value={name}
          onChangeText={setName}
          placeholder="Ej. Mantenimiento"
          placeholderTextColor={tw.color('gray-400')}
          style={tw`bg-gray-100 border border-gray-200 px-4 h-12 rounded-lg mb-4 text-base`}
        />

        <Text style={tw`text-gray-500 mb-2`}>Color</Text>
        <View style={tw`flex-row flex-wrap gap-2 mb-4`}>
          {AREA_COLORS.map(c => (
            <TouchableOpacity
              key={c}
              onPress={() => setColor(c)}
              style={tw.style(
                `w-8 h-8 rounded-full items-center justify-center`,
                color === c && 'border-2 border-gray-700',
                { backgroundColor: c },
              )}
            />
          ))}
        </View>

        <Text style={tw`text-gray-500 mb-2`}>Responsable</Text>
        {usersQuery.isLoading ? (
          <ActivityIndicator size="small" color="#6366f1" />
        ) : (
          <View style={tw`border border-gray-200 rounded-lg overflow-hidden mb-4`}>
            <UserList
              users={users}
              selectedUuid={responsibleUserUuid}
              onSelect={setResponsibleUserUuid}
            />
          </View>
        )}

        <View style={tw`flex-row items-center justify-between mt-2`}>
          {isEditing ? (
            <TouchableOpacity onPress={onDelete} disabled={isSubmitting}>
              <Text style={tw`text-red text-sm`}>Eliminar</Text>
            </TouchableOpacity>
          ) : <View />}

          <IconButton
            disabled={isSubmitting || !name.trim()}
            title={isEditing ? 'Guardar' : 'Crear'}
            onPress={onSave}
          />
        </View>
      </View>
    </Modal>
  );
}

function UserList({ users, selectedUuid, onSelect }: any) {
  return (
    <ScrollView style={tw`max-h-40`} nestedScrollEnabled showsVerticalScrollIndicator>
      <TouchableOpacity
        onPress={() => onSelect(null)}
        style={tw.style(`px-3 py-2`, selectedUuid === null && 'bg-[#FBF5FF]')}
      >
        <Text style={tw`text-sm text-gray-700`}>Sin responsable</Text>
      </TouchableOpacity>
      {users.map((u: any) => (
        <TouchableOpacity
          key={u.uuid}
          onPress={() => onSelect(u.uuid)}
          style={tw.style(`px-3 py-2 border-t border-gray-50`, selectedUuid === u.uuid && 'bg-[#FBF5FF]')}
        >
          <Text style={tw`text-sm text-gray-900`}>{u.name}</Text>
          <Text style={tw`text-xs text-gray-500`}>{u.email}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}
